import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, defer, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { HotelService } from '../../hotel.service';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';

@Component({
  selector: 'mh-hotel-gdpr-retention',
  templateUrl: './hotel-gdpr-retention.component.html',
  styleUrls: ['./hotel-gdpr-retention.component.scss'],
  standalone: false,
})
export class HotelGdprRetentionComponent implements OnInit, OnChanges {
  @Input() customer_id?: number | string;

  gdprForm: FormGroup;
  private gdprConfigId: number | null = null;
  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private hotelService: HotelService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.gdprForm = this.fb.group({
      auto_delete_personal_data: [false],
      deletion_days_after_checkout: [{ value: '', disabled: true }],
    });

    this.gdprForm.get('auto_delete_personal_data')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyActiveState());

    this.applyActiveState();

    if (this.resolveCustomerId()) {
      this.loadGdprConfig();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.gdprForm) {
      return;
    }
    const ch = changes['customer_id'];
    if (ch && !ch.firstChange && this.resolveCustomerId()) {
      this.loadGdprConfig();
    }
  }

  saveGdpr(): Observable<void> {
    return defer(() => {
      const customerId = this.resolveCustomerId();
      if (!customerId) {
        void this.showErrorAlert('errors.generic.message');
        return throwError(() => new Error('no_customer'));
      }
      if (this.gdprConfigId == null) {
        void this.showErrorAlert('errors.generic.message');
        return throwError(() => new Error('no_config'));
      }

      const active = !!this.gdprForm.get('auto_delete_personal_data')?.value;
      const raw = this.gdprForm.getRawValue().deletion_days_after_checkout;
      const days = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);

      if (active && (!Number.isFinite(days) || days < 1)) {
        void this.showErrorAlert('forms.attributes.not_empty');
        return throwError(() => new Error('validation'));
      }

      const body = {
        id: this.gdprConfigId,
        customer_id: customerId,
        days_after_checkout: Number.isFinite(days) && days >= 0 ? days : 0,
        active,
      };

      return this.hotelService.putGdprConfig(customerId, body).pipe(
        mergeMap((res) => {
          if (res?.status === 'OK' && res?.data) {
            this.applyApiDataToForm(res.data);
            void this.showSuccessAlert();
            return of(undefined);
          }
          void this.showErrorAlert('errors.generic.message');
          return throwError(() => new Error('api'));
        }),
        catchError((err) => {
          const skipSecond =
            err instanceof Error &&
            ['api', 'validation', 'no_customer', 'no_config'].includes(err.message);
          if (skipSecond) {
            return throwError(() => err);
          }
          const msg = err?.error?.message || 'errors.generic.message';
          void this.showErrorAlert(typeof msg === 'string' ? msg : 'errors.generic.message');
          return throwError(() => err);
        }),
      );
    });
  }

  private loadGdprConfig(): void {
    const id = this.resolveCustomerId();
    if (!id) {
      return;
    }
    this.gdprForm.disable({ emitEvent: false });
    this.hotelService.getGdprConfig(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res?.status === 'OK' && res?.data) {
          this.applyApiDataToForm(res.data);
        } else {
          void this.showErrorAlert('errors.generic.message');
          this.gdprConfigId = null;
        }
        this.gdprForm.enable({ emitEvent: false });
        this.applyActiveState();
      },
      error: () => {
        void this.showErrorAlert('errors.generic.message');
        this.gdprConfigId = null;
        this.gdprForm.enable({ emitEvent: false });
        this.applyActiveState();
      },
    });
  }

  private applyApiDataToForm(data: {
    id: number;
    customer_id: number;
    days_after_checkout: number;
    active: boolean;
  }): void {
    this.gdprConfigId = data.id;
    this.gdprForm.patchValue(
      {
        auto_delete_personal_data: !!data.active,
        deletion_days_after_checkout: data.days_after_checkout,
      },
      { emitEvent: false },
    );
    this.applyActiveState();
  }

  private applyActiveState(): void {
    const on = !!this.gdprForm.get('auto_delete_personal_data')?.value;
    const daysCtrl = this.gdprForm.get('deletion_days_after_checkout');
    if (on) {
      daysCtrl?.setValidators([Validators.required, Validators.min(1)]);
      daysCtrl?.enable({ emitEvent: false });
    } else {
      daysCtrl?.clearValidators();
      daysCtrl?.disable({ emitEvent: false });
    }
    daysCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  private resolveCustomerId(): number | null {
    const v = this.customer_id;
    if (v === undefined || v === null || v === '') {
      return null;
    }
    const n = typeof v === 'string' ? parseInt(v, 10) : Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private showErrorAlert(message: string): void {
    void this.alertService.handleAlert(message, 5000, 'bottom', 'end', 'snackbar-panel-error-user-config');
  }

  private showSuccessAlert(): void {
    void this.alertService.handleAlert(
      'models.user.data_saved_correctly',
      5000,
      'bottom',
      'end',
      'snackbar-panel-success-user-config',
    );
  }
}
